import {
  Controller,
  Control,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import { round } from 'lodash';
import { Box, Link, TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import Autocomplete from '@mui/material/Autocomplete';
import { useState } from 'react';
import FieldRow from 'components/FormElements/FieldRow';
import AppRadioButton from 'components/FormElements/AppRadioButton';
import AppTextInput from 'components/FormElements/AppTextInput';
import FormLabel from 'components/FormElements/FormLabel';
import ShareClassSelector from 'components/ShareClassSelector';
import TaxReliefElegibleAllocations from './TaxReliefElegibleAllocations';
import Row from 'components/Layout/Row';
import TaxReliefButton from './components/TaxReliefButton';
import {
  TaxReliefElegibleType,
  ConsiderationType,
  ShareConsiderationAllocationMethod,
} from 'further-types/exit';
import { styled, useTheme } from '@mui/material';
import { COMPANY_ADD } from 'adminConstants/routes';
import type { ExitDetailsFormValues } from './index';
import { PreviousExitDetails } from './types';

const StyledRow = styled(Row)(() => ({
  height: '42px',
  gridGap: '1rem',

  '& > div': {
    width: '100%',
  },
}));

const StyledFieldRow = styled(FieldRow)(() => ({
  minHeight: '42px',
}));

const ShareConsiderationLabel = styled(Box)(() => ({
  whiteSpace: 'nowrap',
  minWidth: '80px',
  display: 'flex',
  alignItems: 'center',
  paddingRight: '0rem',
}));

const ShareConsiderationTotalLabel = styled(Box)(() => ({
  whiteSpace: 'nowrap',
  minWidth: '50px',
  display: 'flex',
  alignItems: 'center',
}));

const ShareConsiderationInputWrapper = styled(Box)(() => ({
  flex: 1,
}));

const StyledErrorText = styled('p')(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: theme.fontsizes?.sm,
}));

const StyledRadioButtonGroup = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
}));

type ShareConsiderationFieldsProps = {
  control: Control<ExitDetailsFormValues>;
  setValue: UseFormSetValue<ExitDetailsFormValues>;
  errors: FieldErrors<ExitDetailsFormValues>;
  isEditMode: boolean;
  disableEdit: boolean;
  considerationType: ConsiderationType | undefined;
  shareConsiderationCompanyId: string | undefined;
  totalConsiderationShares: string;
  shareConsiderationAllocationMethod:
    | ShareConsiderationAllocationMethod
    | undefined;
  taxReliefElegible?: TaxReliefElegibleType;
  firmId: string | undefined;
  selectedShareClasses: Array<string>;
  isBeingCorrected?: boolean;
  isCorrectingAmendment?: boolean;
  selectedAllocations: Array<string>;
  taxReliefElegibleAllocationIds: Array<string>;
  filteredConsiderationCompanies: Array<{
    _id: string;
    tradingName?: string;
    legalName?: string;
  }>;
  previousExitDetails?: PreviousExitDetails;
  lastManuallyEditedField: React.MutableRefObject<
    'perShare' | 'total' | 'shares' | null
  >;
  calculateShareConsiderationValues: (
    changedField: 'perShare' | 'total' | 'shares',
    newValue?: string,
  ) => void;
  hasTaxableValueIncrease: boolean;
  originalTaxReliefElegible?: TaxReliefElegibleType;
};

const ShareConsiderationFields: React.FC<ShareConsiderationFieldsProps> = ({
  control,
  setValue,
  errors,
  isEditMode,
  disableEdit,
  considerationType,
  shareConsiderationCompanyId,
  totalConsiderationShares,
  shareConsiderationAllocationMethod,
  taxReliefElegible,
  firmId,
  selectedShareClasses,
  isBeingCorrected,
  isCorrectingAmendment,
  selectedAllocations,
  taxReliefElegibleAllocationIds,
  filteredConsiderationCompanies,
  previousExitDetails,
  lastManuallyEditedField,
  calculateShareConsiderationValues,
  hasTaxableValueIncrease,
  originalTaxReliefElegible,
}) => {
  const theme = useTheme();
  const [focusedField, setFocusedField] = useState<
    'totalShares' | 'perShare' | 'total' | null
  >(null);

  // When correcting the original exit (not an amendment), no restrictions should apply
  const isCorrectingOriginalExit = isBeingCorrected && !isCorrectingAmendment;

  // Compute greying-out conditions for S/EIS eligible exit field
  // Restrictions only apply when amending or editing, not when correcting the original
  const isEntireFieldDisabled =
    isEditMode && !hasTaxableValueIncrease && !isCorrectingOriginalExit;

  // Disable Yes when original was No or Partial
  const isYesDisabled =
    !isCorrectingOriginalExit &&
    isEditMode &&
    hasTaxableValueIncrease &&
    (originalTaxReliefElegible === TaxReliefElegibleType.No ||
      originalTaxReliefElegible === TaxReliefElegibleType.Partial);

  // Disable No only when original was No (not Partial)
  const isNoDisabled =
    !isCorrectingOriginalExit &&
    isEditMode &&
    hasTaxableValueIncrease &&
    originalTaxReliefElegible === TaxReliefElegibleType.No;

  // Disable Partial only when original was No
  const isPartialDisabled =
    !isCorrectingOriginalExit &&
    isEditMode &&
    hasTaxableValueIncrease &&
    originalTaxReliefElegible === TaxReliefElegibleType.No;

  const getDisplayValue = (
    value: string | number | undefined,
    fieldName: 'totalShares' | 'perShare' | 'total',
  ): string => {
    // Only show empty when focused and value is exactly '0' (not when editing non-zero values)
    const stringValue = value?.toString() || '';
    if (focusedField === fieldName && stringValue === '0') {
      return '';
    }
    return stringValue;
  };

  if (considerationType !== ConsiderationType.Share) {
    return null;
  }

  return (
    <>
      <StyledFieldRow
        title="Share consideration company"
        centerTitle
        tooltipText={
          <span>
            Select a company that exists in the system. If you need to add a new
            company or share class,{' '}
            <Link href={COMPANY_ADD} target="_blank" rel="noopener noreferrer">
              click here
            </Link>{' '}
            to create that company.
          </span>
        }
      >
        <StyledRow>
          <Controller
            name="shareConsiderationCompanyId"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                options={filteredConsiderationCompanies.map((investee) => ({
                  id: investee._id,
                  label: `${investee.tradingName} - ${investee.legalName}`,
                }))}
                getOptionLabel={(option) => option.label || ''}
                getOptionKey={(option) => option.id}
                value={
                  filteredConsiderationCompanies
                    .map((investee) => ({
                      id: investee._id,
                      label: `${investee.tradingName} - ${investee.legalName}`,
                    }))
                    .find((c) => c.id === field.value) || null
                }
                onChange={(_event, selectedCompany) => {
                  field.onChange(selectedCompany?.id);
                  setValue('shareConsiderationShareClassId', undefined);
                  if (!isEditMode) {
                    setValue('totalConsiderationShares', '0');
                    setValue('shareConsiderationValuePerShare', '0');
                    setValue('shareConsiderationValueTotal', '0');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select company"
                    error={!!error}
                    helperText={error?.message}
                    disabled={disableEdit}
                  />
                )}
              />
            )}
          />
        </StyledRow>
      </StyledFieldRow>
      {shareConsiderationCompanyId && (
        <>
          <StyledFieldRow title="Consideration share class" centerTitle>
            <StyledRow>
              <Controller
                name="shareConsiderationShareClassId"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <ShareClassSelector
                    selectedCompanyId={shareConsiderationCompanyId}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!error}
                    helperText={error?.message}
                    disabled={disableEdit}
                  />
                )}
              />
            </StyledRow>
          </StyledFieldRow>
          <StyledFieldRow title="Total consideration shares" centerTitle>
            <StyledRow>
              <Controller
                name="totalConsiderationShares"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <NumericFormat
                    onValueChange={({ value }) => {
                      field.onChange(value);
                      lastManuallyEditedField.current = 'shares';
                      calculateShareConsiderationValues('shares', value);
                    }}
                    value={getDisplayValue(field.value, 'totalShares')}
                    onFocus={() => {
                      setFocusedField('totalShares');
                      // Only clear if value is exactly '0' - this allows normal backspace on non-zero values
                      if (field.value === '0') {
                        field.onChange('');
                      }
                    }}
                    onBlur={(_e) => {
                      setFocusedField(null);
                      if (!field.value || field.value === '') {
                        field.onChange('0');
                      }
                      field.onBlur();
                    }}
                    allowNegative={false}
                    allowLeadingZeros={false}
                    thousandSeparator={true}
                    customInput={AppTextInput}
                    error={!!error}
                    helperText={error?.message}
                    disabled={disableEdit}
                    placeholder="0"
                  />
                )}
              />
            </StyledRow>
          </StyledFieldRow>

          <StyledFieldRow
            title="Share consideration value"
            centerTitle
            tooltipText={
              'This is the notional value ascribed to the consideration shares. You can enter either the ‘per share’ or ‘total’ price. For example, if you are selling an existing holding for £1,000,000 total value and receiving 50% share consideration, the notional value will be £500,000. If there are 500,000 shares, the price will be £1.00 per share.'
            }
          >
            <Row spacing="sm" fullWidth>
              <ShareConsiderationLabel>
                <FormLabel
                  title="Per share"
                  labelColor={theme.palette.text.secondary}
                />
              </ShareConsiderationLabel>
              <ShareConsiderationInputWrapper>
                <Controller
                  name="shareConsiderationValuePerShare"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <NumericFormat
                      onValueChange={({ value }) => {
                        // Normalize the value to remove any floating point artifacts
                        // This ensures values like 2.2302 stay as 2.2302, not 2.2302000027
                        const numValue = Number(value);
                        const normalizedValue = isNaN(numValue)
                          ? value
                          : round(numValue, 10).toString();
                        field.onChange(normalizedValue);
                        lastManuallyEditedField.current = 'perShare';
                        calculateShareConsiderationValues(
                          'perShare',
                          normalizedValue,
                        );
                      }}
                      value={getDisplayValue(field.value, 'perShare')}
                      onFocus={(e) => {
                        setFocusedField('perShare');
                        if (field.value === '0') {
                          field.onChange('');
                        }
                        // Prevent select-all behavior on focus by positioning cursor at end
                        // This allows normal character-by-character deletion with backspace
                        const target = e.target as HTMLInputElement;
                        if (target?.value) {
                          // Use a microtask to check after NumericFormat has processed focus
                          Promise.resolve().then(() => {
                            if (target && document.activeElement === target) {
                              const selectionStart = target.selectionStart ?? 0;
                              const selectionEnd = target.selectionEnd ?? 0;
                              // If all text is selected (default browser behavior), position cursor at end
                              // This prevents backspace from clearing everything
                              if (
                                selectionStart === 0 &&
                                selectionEnd === target.value.length
                              ) {
                                const len = target.value.length;
                                target.setSelectionRange(len, len);
                              }
                            }
                          });
                        }
                      }}
                      onBlur={(_e) => {
                        setFocusedField(null);
                        if (!field.value || field.value === '') {
                          field.onChange('0');
                        }
                        field.onBlur();
                      }}
                      prefix="£"
                      decimalScale={10}
                      allowNegative={false}
                      allowLeadingZeros={false}
                      thousandSeparator={true}
                      customInput={AppTextInput}
                      error={!!error}
                      helperText={error?.message}
                      disabled={disableEdit}
                      size="small"
                      style={{ paddingRight: '2em' }}
                      placeholder="0"
                    />
                  )}
                />
              </ShareConsiderationInputWrapper>
              <ShareConsiderationTotalLabel>
                <FormLabel
                  title="Total"
                  labelColor={theme.palette.text.secondary}
                />
              </ShareConsiderationTotalLabel>
              <ShareConsiderationInputWrapper>
                <Controller
                  name="shareConsiderationValueTotal"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <NumericFormat
                      onValueChange={({ value }) => {
                        field.onChange(value);
                        lastManuallyEditedField.current = 'total';
                        calculateShareConsiderationValues('total', value);
                      }}
                      value={getDisplayValue(field.value, 'total')}
                      onFocus={() => {
                        setFocusedField('total');
                        if (field.value === '0') {
                          field.onChange('');
                        }
                      }}
                      onBlur={(_e) => {
                        setFocusedField(null);
                        if (!field.value || field.value === '') {
                          field.onChange('0');
                        }
                        field.onBlur();
                      }}
                      prefix="£"
                      decimalScale={2}
                      allowNegative={false}
                      allowLeadingZeros={false}
                      thousandSeparator={true}
                      customInput={AppTextInput}
                      error={!!error}
                      helperText={error?.message}
                      disabled={disableEdit}
                      size="small"
                      placeholder="0"
                    />
                  )}
                />
              </ShareConsiderationInputWrapper>
            </Row>
          </StyledFieldRow>

          <StyledFieldRow
            title="Shares part of initial consideration"
            centerTitle
            tooltipText={
              'If these consideration shares were issued in the initial sale (i.e. they were not part of an earnout), ensure this toggle is on. This setting is used for tax reporting purposes.'
            }
          >
            <Controller
              name="isInitialConsideration"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <AppRadioButton
                    name="isInitialConsideration"
                    label="Yes"
                    value={1}
                    checked={field.value === true}
                    onChange={(_, checked) => {
                      field.onChange(checked);
                    }}
                    error={!!error}
                    disabled={disableEdit}
                  />
                  <AppRadioButton
                    name="isInitialConsideration"
                    label="No"
                    value={0}
                    checked={field.value === false}
                    onChange={(_, checked) => {
                      field.onChange(!checked);
                    }}
                    error={!!error}
                    disabled={disableEdit}
                  />
                  {error && <StyledErrorText>{error.message}</StyledErrorText>}
                </>
              )}
            />
          </StyledFieldRow>

          <StyledFieldRow
            title="S/EIS eligible share consideration"
            centerTitle
            tooltipText={
              <span>
                If the consideration shares will receive continued EIS
                treatment, select "Yes". If you are unsure, please seek
                professional tax advice before proceeding. Your selection here
                will change how the system reports taxable gains or losses from
                these shares in future.
              </span>
            }
          >
            <Controller
              name="shareConsiderationEisEligible"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <AppRadioButton
                    name="shareConsiderationEisEligible"
                    label="Yes"
                    value={1}
                    checked={field.value === true}
                    onChange={(_, checked) => {
                      field.onChange(checked);
                    }}
                    error={!!error}
                    disabled={disableEdit}
                  />
                  <AppRadioButton
                    name="shareConsiderationEisEligible"
                    label="No"
                    value={0}
                    checked={field.value === false}
                    onChange={(_, checked) => {
                      field.onChange(!checked);
                    }}
                    error={!!error}
                    disabled={disableEdit}
                  />
                  {error && <StyledErrorText>{error.message}</StyledErrorText>}
                </>
              )}
            />
          </StyledFieldRow>

          <>
            <StyledFieldRow
              title="S/EIS eligible exit"
              centerTitle
              tooltipText={
                <span>
                  {isEntireFieldDisabled && (
                    <>
                      <span>
                        This exit's S/EIS eligible status has already been set.
                        If there is no taxable value increase, an edit must
                        follow the original exit's tax status.
                      </span>
                      <br />
                      <br />
                    </>
                  )}
                  <span>
                    Editing the S/EIS status of this exit will change the status
                    from the date of this update, not the original exit date. If
                    you have received earnout consideration which increases the
                    taxable value share price, this consideration is not
                    typically S/EIS eligible and this update should be marked
                    'No' unless you have received tax advice otherwise. Receipt
                    of escrow release proceeds is typically S/EIS eligible.
                  </span>
                </span>
              }
            >
              <Controller
                name="taxReliefElegible"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <TaxReliefButton
                      option={TaxReliefElegibleType.Yes}
                      checked={field.value === TaxReliefElegibleType.Yes}
                      onChange={field.onChange}
                      error={!!error}
                      disableEdit={disableEdit}
                      isEntireFieldDisabled={isEntireFieldDisabled}
                      isYesDisabled={isYesDisabled}
                      isNoDisabled={isNoDisabled}
                      isPartialDisabled={isPartialDisabled}
                      originalTaxReliefElegible={originalTaxReliefElegible}
                    />
                    <TaxReliefButton
                      option={TaxReliefElegibleType.No}
                      checked={field.value === TaxReliefElegibleType.No}
                      onChange={field.onChange}
                      error={!!error}
                      disableEdit={disableEdit}
                      isEntireFieldDisabled={isEntireFieldDisabled}
                      isYesDisabled={isYesDisabled}
                      isNoDisabled={isNoDisabled}
                      isPartialDisabled={isPartialDisabled}
                      originalTaxReliefElegible={originalTaxReliefElegible}
                    />
                    <TaxReliefButton
                      option={TaxReliefElegibleType.Partial}
                      checked={field.value === TaxReliefElegibleType.Partial}
                      onChange={field.onChange}
                      error={!!error}
                      disableEdit={disableEdit}
                      isEntireFieldDisabled={isEntireFieldDisabled}
                      isYesDisabled={isYesDisabled}
                      isNoDisabled={isNoDisabled}
                      isPartialDisabled={isPartialDisabled}
                      originalTaxReliefElegible={originalTaxReliefElegible}
                    />
                    {error && (
                      <StyledErrorText>{error.message}</StyledErrorText>
                    )}
                  </>
                )}
              />
            </StyledFieldRow>
            {taxReliefElegible === TaxReliefElegibleType.Partial &&
              considerationType === ConsiderationType.Share &&
              (isEditMode
                ? previousExitDetails?.firmId &&
                  Array.isArray(previousExitDetails?.selectedShareClasses) &&
                  previousExitDetails.selectedShareClasses.length > 0 &&
                  Array.isArray(
                    previousExitDetails?.shareAllocationUploadIds,
                  ) &&
                  previousExitDetails.shareAllocationUploadIds.length > 0
                : firmId &&
                  selectedShareClasses.length > 0 &&
                  selectedAllocations.length > 0) && (
                <TaxReliefElegibleAllocations
                  firmId={firmId ?? ''}
                  selectedShareClasses={
                    isEditMode
                      ? previousExitDetails?.selectedShareClasses ?? []
                      : selectedShareClasses
                  }
                  selectedAllocations={
                    isEditMode
                      ? previousExitDetails?.shareAllocationUploadIds ?? []
                      : selectedAllocations
                  }
                  taxReliefElegibleAllocationIds={
                    taxReliefElegibleAllocationIds
                  }
                  onChange={(value) =>
                    setValue('taxReliefElegibleAllocationIds', value)
                  }
                  disabled={disableEdit}
                  error={errors.taxReliefElegibleAllocationIds?.message}
                />
              )}
          </>

          {!isBeingCorrected &&
            totalConsiderationShares &&
            Number(totalConsiderationShares) > 0 && (
              <StyledFieldRow title="Allocation method" labelSpacing={12}>
                <Controller
                  name="shareConsiderationAllocationMethod"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <StyledRadioButtonGroup>
                      <AppRadioButton
                        name="shareConsiderationAllocationMethod"
                        label="Upload allocation spreadsheet"
                        value={ShareConsiderationAllocationMethod.Upload}
                        checked={
                          field.value ===
                          ShareConsiderationAllocationMethod.Upload
                        }
                        onChange={(_, checked) => {
                          if (checked) {
                            field.onChange(
                              ShareConsiderationAllocationMethod.Upload,
                            );
                          }
                        }}
                        error={!!error}
                        disabled={disableEdit}
                      />
                      <AppRadioButton
                        name="shareConsiderationAllocationMethod"
                        label="Automatic pro-rata allocation"
                        value={ShareConsiderationAllocationMethod.Automatic}
                        checked={
                          field.value ===
                          ShareConsiderationAllocationMethod.Automatic
                        }
                        onChange={(_, checked) => {
                          if (checked) {
                            field.onChange(
                              ShareConsiderationAllocationMethod.Automatic,
                            );
                          }
                        }}
                        error={!!error}
                        disabled={disableEdit}
                      />
                      {error && (
                        <StyledErrorText>{error.message}</StyledErrorText>
                      )}
                    </StyledRadioButtonGroup>
                  )}
                />
              </StyledFieldRow>
            )}
          {shareConsiderationAllocationMethod ===
            ShareConsiderationAllocationMethod.Automatic &&
            totalConsiderationShares &&
            Number(totalConsiderationShares) > 0 && (
              <StyledFieldRow
                title="Permit partial share allocation"
                centerTitle
                tooltipText={
                  <span>
                    If turned on, the system will allocate shares exactly
                    pro-rata, meaning investors are likely to receive a partial
                    share. If turned off, the system will allocate whole shares
                    pro rata and distribute leftovers to investors from the
                    largest down to the smallest.
                  </span>
                }
              >
                <Controller
                  name="permitPartialShareAllocation"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <AppRadioButton
                        name="permitPartialShareAllocation"
                        label="Yes"
                        value={1}
                        checked={field.value}
                        onChange={(_, checked) => {
                          if (checked) {
                            field.onChange(true);
                          }
                        }}
                        error={!!error}
                        disabled={disableEdit}
                      />
                      <AppRadioButton
                        name="permitPartialShareAllocation"
                        label="No"
                        value={0}
                        checked={!field.value}
                        onChange={(_, checked) => {
                          if (checked) {
                            field.onChange(false);
                          }
                        }}
                        error={!!error}
                        disabled={disableEdit}
                      />
                      {error && (
                        <StyledErrorText>{error.message}</StyledErrorText>
                      )}
                    </>
                  )}
                />
              </StyledFieldRow>
            )}
        </>
      )}
    </>
  );
};

export default ShareConsiderationFields;
